import React from "react";

/**
 * Template #1 — box_flow (3D Isometric)
 * Explains: Processes, Pipelines, Steps, Data flow, Cause → Effect
 * Parts: box_1, box_2, box_3, arrow_1, arrow_2
 * Actions: draw, highlight, fade, zoom, label, arrow, animate_flow
 */

export interface BoxFlowProps {
  visibleParts?: string[];
  highlightTarget?: string;
  fadedParts?: string[];
  labels?: [string, string, string];
  opacity?: number;
  flowProgress?: number;
  accentColor?: string;
}

export const BoxFlow: React.FC<BoxFlowProps> = ({
  visibleParts,
  highlightTarget,
  fadedParts = [],
  labels = ["Step 1", "Step 2", "Step 3"],
  opacity = 1,
  flowProgress,
  accentColor = "#6366f1",
}) => {
  const allVisible = !visibleParts;
  const isVisible = (part: string) => allVisible || visibleParts!.includes(part);
  const isFaded = (part: string) => fadedParts.includes(part);
  const isHighlighted = (part: string) => highlightTarget === part;

  // Isometric box geometry
  const w = 130;
  const h = 60;
  const depth = 22;
  const gap = 85;
  const startX = 95;
  const baseY = 210;

  const boxes = [
    { id: "box_1", x: startX, label: labels[0] },
    { id: "box_2", x: startX + w + gap, label: labels[1] },
    { id: "box_3", x: startX + (w + gap) * 2, label: labels[2] },
  ];

  const arrows = [
    { id: "arrow_1", x1: boxes[0].x + w, x2: boxes[1].x },
    { id: "arrow_2", x1: boxes[1].x + w, x2: boxes[2].x },
  ];

  const uid = "bf" + accentColor.replace("#", "");

  const wrapLabel = (text: string, maxChars: number, maxLines: number) => {
    const words = text.split(" ").filter(Boolean);
    if (words.length === 0) return [""];
    const lines: string[] = [];
    let current = "";
    words.forEach((word) => {
      const next = current ? `${current} ${word}` : word;
      if (next.length <= maxChars || current.length === 0) {
        current = next;
      } else {
        lines.push(current);
        current = word;
      }
    });
    if (current) lines.push(current);
    return lines.slice(0, maxLines);
  };

  const renderIsoBox = (id: string, bx: number, label: string) => {
    const hl = isHighlighted(id);
    const fd = isFaded(id);
    const o = fd ? 0.35 : 1;
    const labelLines = wrapLabel(label, 12, 2);
    const fontSize = label.length > 14 ? 10 : 12;
    const lineHeight = fontSize + 2;
    const labelY = baseY - h / 2 - ((labelLines.length - 1) * lineHeight) / 2;

    // Three faces of the iso box
    const front = `M${bx},${baseY} L${bx + w},${baseY} L${bx + w},${baseY - h} L${bx},${baseY - h} Z`;
    const top = `M${bx},${baseY - h} L${bx + depth},${baseY - h - depth} L${bx + w + depth},${baseY - h - depth} L${bx + w},${baseY - h} Z`;
    const side = `M${bx + w},${baseY} L${bx + w + depth},${baseY - depth} L${bx + w + depth},${baseY - h - depth} L${bx + w},${baseY - h} Z`;

    const frontBase = hl ? accentColor : "#1e293b";
    const topBase = hl ? accentColor : "#334155";
    const sideBase = hl ? accentColor : "#0f172a";
    const stroke = hl ? "#a5b4fc" : "#475569";

    return (
      <g key={id} opacity={o}>
        {/* Drop shadow */}
        <ellipse cx={bx + w / 2 + depth / 2} cy={baseY + 12} rx={w * 0.55} ry={7} fill="#000" opacity={hl ? 0.3 : 0.12} />

        {/* Glow */}
        {hl && (
          <rect x={bx - 8} y={baseY - h - depth - 8} width={w + depth + 16} height={h + depth + 24} rx={12} fill={accentColor} opacity={0.08} />
        )}

        {/* Front face — gradient top-to-bottom */}
        <path d={front} fill={frontBase} stroke={stroke} strokeWidth={1.2} />
        <path d={front} fill="url(#frontShine)" opacity={hl ? 0.25 : 0.1} />

        {/* Top face — lightest */}
        <path d={top} fill={topBase} stroke={stroke} strokeWidth={1.2} />
        <path d={top} fill="#fff" opacity={hl ? 0.18 : 0.06} />

        {/* Right side — darkest */}
        <path d={side} fill={sideBase} stroke={stroke} strokeWidth={1.2} />

        {/* Top edge specular */}
        <line x1={bx + 4} y1={baseY - h} x2={bx + w - 4} y2={baseY - h} stroke="#fff" strokeWidth={0.7} opacity={hl ? 0.35 : 0.07} />

        {/* Label */}
        <text
          x={bx + w / 2}
          y={labelY}
          fill={fd ? "#64748b" : "#f1f5f9"}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={fontSize}
          fontWeight={600}
          fontFamily="'Inter', system-ui, sans-serif"
        >
          {labelLines.map((line, idx) => (
            <tspan key={`${id}-line-${idx}`} x={bx + w / 2} dy={idx === 0 ? 0 : lineHeight}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };

  const renderArrow = (id: string, x1: number, x2: number) => {
    const fd = isFaded(id);
    const ay = baseY - h / 2;
    const ax1 = x1 + depth + 10;
    const ax2 = x2 - 8;

    return (
      <g key={id} opacity={fd ? 0.25 : 0.85}>
        {/* Shadow line */}
        <line x1={ax1} y1={ay + 2} x2={ax2 - 8} y2={ay + 2} stroke="#000" strokeWidth={4} opacity={0.15} strokeLinecap="round" />
        {/* Main shaft */}
        <line
          x1={ax1}
          y1={ay}
          x2={ax2 - 8}
          y2={ay}
          stroke={accentColor}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeDasharray={flowProgress !== undefined ? "8 5" : "none"}
          strokeDashoffset={flowProgress !== undefined ? -flowProgress * 120 : 0}
        />
        {/* Glow line */}
        <line x1={ax1} y1={ay} x2={ax2 - 8} y2={ay} stroke="#fff" strokeWidth={1} opacity={0.15} strokeLinecap="round" />
        {/* Arrowhead */}
        <polygon points={`${ax2 - 6},${ay - 7} ${ax2 + 4},${ay} ${ax2 - 6},${ay + 7}`} fill={accentColor} />
        <polygon points={`${ax2 - 4},${ay - 4} ${ax2 + 2},${ay} ${ax2 - 4},${ay + 4}`} fill="#fff" opacity={0.2} />
      </g>
    );
  };

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="frontShine" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.05" />
        </linearGradient>
      </defs>

      {/* Ground reflection line */}
      <rect x={startX - 10} y={baseY + 18} width={(w + gap) * 2 + w + depth + 20} height={1} fill="#475569" opacity={0.2} rx={1} />

      {arrows.map((a) => isVisible(a.id) ? renderArrow(a.id, a.x1, a.x2) : null)}
      {boxes.map((b) => isVisible(b.id) ? renderIsoBox(b.id, b.x, b.label) : null)}
    </g>
  );
};
